/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2020, Zumper
 * @description   Message Form Inputs Container
 */

import { Box } from '@chakra-ui/react'
import type { ButtonProps } from '@zcode-ui/controls'
import {
  loadAddressDetails,
  selectActiveMessageableListings,
  selectMessageListingDetails,
  setMessageActiveListingId,
} from '@zcode-ui/perm-address'
import { checkLoggedIn, getUser, selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import type { User } from '@zcode/bizos'
import type { AnalyticsC, Origin } from '@zcode/nr-utils'
import {
  selectIsMultifamily,
  selectIsMultifamilyTour,
} from '@zcode/nr/es/shared/bizos/multifamily/selectors'
import ScreenReaderOnly from '@zcode/nr/es/shared/components/ScreenReaderOnly'
import { type APPLY, type MESSAGE, TOUR, WAITLIST } from '@zcode/nr/es/shared/constants/message'
import type {
  FloorplanListing,
  Listable,
  Listing,
  MessageModal,
  NormalFloorplanListingData,
  Property,
  State,
} from '@zcode/state-type'
import { useFormikContext } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import type { Dispatch } from 'redux'
import { trackMessageFieldConversionEvent } from '../../../../modules/traktor/thunks/trackMessageFieldConversionEvent'
import { getUrlParams } from '../../../../utils/urls/getUrlParams'
import { useHideTimeAndTypePicker } from '../../hooks/useHideTimeAndTypePicker'
import type { Values } from '../helpers'
import { getHasMessaged } from '../helpers/getHasMessaged'
import { FormInputs, type FormInputsProps } from './FormInputs'
import { TourFormStep1 } from './TourFormStep1'

const mapStateToProps = (state: State) => {
  return {
    isLoggedIn: checkLoggedIn(state),
    isMobile: selectIsMobile(state),
    user: getUser(state),
    listings: selectActiveMessageableListings(state),
    selectedListing: selectMessageListingDetails(state),
    urlParams: getUrlParams(),
  } satisfies Partial<FormInputsInnerContainerProps>
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onTrackConversion: ({ key, value }: { key: string; value: string }) => {
      dispatch(trackMessageFieldConversionEvent({ key, value }))
    },
    selectListing: (listingId: number) => {
      dispatch(setMessageActiveListingId(listingId))
      void dispatch(loadAddressDetails(listingId))
    },
  } satisfies Partial<FormInputsInnerContainerProps>
}

interface FormInputsInnerContainerProps {
  allowMFTourButton: boolean | undefined
  analyticChannel:
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.HOMEPAGE
  defaultMessageRef: React.MutableRefObject<string | undefined>
  entity: Listable | Property
  equalCTAIntent: typeof APPLY | typeof MESSAGE | typeof TOUR | typeof WAITLIST | undefined
  hideCustomMessage: boolean | undefined
  hideFloorplans: boolean | undefined
  hideMoveInDate?: boolean
  isModal: boolean | undefined
  includesTourTimes?: boolean
  isLoggedIn: boolean
  isMobile: boolean
  listings: ReturnType<typeof selectActiveMessageableListings>
  onSetFloorplanUserSelected: (e: React.ChangeEvent<HTMLSelectElement>) => void
  onTrackConversion: (args: { key: string; value: string }) => void
  onlyShowFloorplanGroup: boolean | undefined
  origin: Origin
  phoneNumberRequired: boolean
  selectedFloorplan: FloorplanListing<NormalFloorplanListingData> | undefined
  selectedListing: Listing | null
  selectListing: (listingId: number) => void
  step: MessageModal['step']
  submitButton: React.ReactElement<ButtonProps<'button'>>
  urlParams: ReturnType<typeof getUrlParams>
  user: User
}

const FormInputsInnerContainer = ({
  allowMFTourButton,
  analyticChannel,
  entity,
  equalCTAIntent,
  hideCustomMessage,
  hideMoveInDate,
  includesTourTimes,
  isLoggedIn,
  isMobile,
  onTrackConversion: propOnTrackConversion,
  step,
  submitButton,
  user,
  selectedFloorplan,
  ...forwardProps
}: FormInputsInnerContainerProps) => {
  const { floorplans, isBuilding, isCustomMessageRequired, isMoveInDateRequired } = entity

  const [enableEdit, setEnableEdit] = useState(false)
  const [tracked, setTracked] = useState(new Set())
  const { avatarName, fullName } = user
  const isMultifamily = selectIsMultifamily(entity)
  const floorplanRequired = isMultifamily && isBuilding
  const formik = useFormikContext<Values>()
  const onTrackConversion = useCallback(
    (
      event:
        | React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
        | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
      const { target } = event
      const { name: key, value } = target
      const hasMessaged = getHasMessaged()
      const shouldTrackConversion = !hasMessaged && formik.dirty

      if (shouldTrackConversion) {
        typeof propOnTrackConversion === 'function' && propOnTrackConversion({ key, value })
        tracked.add(key)
        setTracked(tracked)
      }

      // eslint-disable-next-line no-unused-expressions
      formik.handleBlur(event)
    },
    [formik, propOnTrackConversion, tracked]
  )

  const shouldChangeText = entity?.isActive && isMultifamily && equalCTAIntent !== WAITLIST
  const { skipTourFormStep1 } = useHideTimeAndTypePicker(entity)
  const isSingleStepTourForm = step === null && equalCTAIntent === TOUR && !skipTourFormStep1
  const isSubmittingLastStep = step !== 1 && formik.isSubmitting

  const hiddenInputsErrors = !!formik.errors.name || !!formik.errors.name || !!formik.errors.phone
  useEffect(() => {
    if (isSubmittingLastStep && hiddenInputsErrors) {
      setEnableEdit(true)
    }
  }, [isSubmittingLastStep, hiddenInputsErrors])

  if (!formik) {
    return null
  }
  const {
    errors,
    handleChange,
    isValid,
    resetForm,
    setFieldTouched,
    setFieldValue,
    submitCount,
    touched,
    values,
  } = formik

  const hideInputs = !enableEdit

  const showCustomMessage = !hideCustomMessage || isCustomMessageRequired
  const showMessageInput = isSingleStepTourForm
    ? (!hideInputs || isLoggedIn || !!values.name) && showCustomMessage
    : showCustomMessage
  const isMultifamilyTour = selectIsMultifamilyTour(entity)
  const showMultifamilyTour = isMultifamilyTour && allowMFTourButton
  const shouldRenderDateInputs =
    !hideMoveInDate ||
    isMoveInDateRequired ||
    isMultifamily ||
    showMultifamilyTour ||
    equalCTAIntent === TOUR
  const moveInRequired =
    isMultifamily || isMoveInDateRequired || showMultifamilyTour || equalCTAIntent === TOUR
  const showTourFormStep1 = (step === 1 || isSingleStepTourForm) && submitButton

  // biome-ignore lint/correctness/noConstantCondition:
  if (false) {
    // verify that we're not passing any properites to <FormInputs /> that it doesn't need
    let x: Exclude<keyof typeof forwardProps, keyof FormInputsProps>
    x satisfies never
  }

  return (
    <>
      <div aria-live="assertive" role="alert">
        <ScreenReaderOnly>
          <span id={forwardProps.isModal ? 'modal-form-errors' : 'sticky-form-errors'}>
            {Object.keys(touched).length && !isValid
              ? 'Your form has errors. Please fix them and submit again.'
              : null}
          </span>
        </ScreenReaderOnly>
      </div>
      {showTourFormStep1 ? (
        <TourFormStep1
          entity={entity}
          errors={errors}
          isSubmitting={formik.isSubmitting}
          onTrackConversion={onTrackConversion}
          origin={forwardProps.origin}
          resetForm={resetForm}
          setFieldValue={setFieldValue}
          submitButton={submitButton}
          submitCount={submitCount}
          touched={touched}
          values={values}
        />
      ) : null}
      {isSingleStepTourForm ? (
        <Box<'div'> backgroundColor="gray.150" height="1px" marginBottom={{ base: 6, lg: 4 }} />
      ) : null}
      {step !== 1 ? (
        <FormInputs
          avatarName={avatarName}
          entity={entity}
          equalCTAIntent={equalCTAIntent}
          errors={errors}
          floorplanRequired={floorplanRequired}
          floorplans={floorplans}
          fullName={fullName}
          hideInputs={hideInputs}
          initialPhoneValue={formik.initialValues.phone}
          initialValues={formik.initialValues}
          isBuilding={isBuilding}
          isLoggedIn={isLoggedIn}
          isMobile={isMobile}
          isMultifamily={isMultifamily}
          moveInRequired={moveInRequired}
          onChange={handleChange}
          onShowInputs={() => setEnableEdit(true)}
          onTrackConversion={onTrackConversion}
          selectedFloorplan={selectedFloorplan}
          setFieldTouched={setFieldTouched}
          setFieldValue={setFieldValue}
          shouldChangeText={shouldChangeText}
          shouldRenderDateInputs={shouldRenderDateInputs}
          showMessageInput={showMessageInput}
          showMultifamilyTour={showMultifamilyTour}
          touched={touched}
          values={values}
          {...(forwardProps satisfies Partial<FormInputsProps>)}
        />
      ) : null}
    </>
  )
}

export const FormInputsContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(FormInputsInnerContainer)
